import { buildCode39SvgModel } from "@/core/printing/code39-barcode";
import {
  labelPrintSizeHint,
  loadSavedLabelFormat,
} from "@/core/printing/label-print-formats";
import { loadSavedLabelRotation } from "@/core/printing/label-print-rotation";
import type { ProductLabelData } from "./ProductLabelPrintSheet.types";
import {
  printLabelDocument,
  printLabelDocumentBridge,
  printLabelDocumentDirect,
  resolveAutoPrintMode,
  type PrintLabelOptions,
} from "./print-product-label";

export type { ProductLabelData } from "./ProductLabelPrintSheet.types";
export {
  LABEL_PRINT_FORMATS,
  getGroupedLabelPrintFormats,
  loadSavedLabelFormat,
  saveLabelFormat,
  labelPrintSizeHint,
  type LabelPrintFormat,
} from "@/core/printing/label-print-formats";
export {
  loadSavedLabelRotation,
  saveLabelRotation,
  type LabelPrintRotation,
} from "@/core/printing/label-print-rotation";
export { isWebSerialLabelPrintAvailable } from "@/core/printing/label-print-tspl";
export type { PrintLabelOptions, LabelPrintMode } from "./print-product-label";

interface ProductLabelPrintSheetProps {
  label: ProductLabelData;
}

/** Contenu étiquette (aperçu écran — mise à l'échelle par LabelPrintPreview). */
export function ProductLabelPrintSheet({ label }: ProductLabelPrintSheetProps) {
  const model = buildCode39SvgModel(label.barcode);
  const qtyLine =
    label.quantity != null && label.quantity > 0
      ? `Qté reçue : ${label.quantity}${label.unit ? ` ${label.unit}` : ""}`
      : null;

  return (
    <div className="ph-product-label-sheet">
      <div className="ph-product-label-brand">ITaaS Hospitol</div>
      <div className="ph-product-label-name">{label.productName}</div>
      {label.sku && label.sku !== label.barcode && (
        <div className="ph-product-label-sku">Réf. {label.sku}</div>
      )}
      <svg
        className="ph-product-label-barcode"
        viewBox={`0 0 ${model.width} ${model.height}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label={`Code-barres ${model.humanText}`}
      >
        {model.bars.map((bar, index) => (
          <rect
            key={`${bar.x}-${index}`}
            x={bar.x}
            y={0}
            width={bar.width}
            height={model.height}
            fill="#000"
          />
        ))}
      </svg>
      <div className="ph-product-label-code">{model.humanText}</div>
      {qtyLine && <div className="ph-product-label-qty">{qtyLine}</div>}
    </div>
  );
}

export async function printProductLabel(
  label: ProductLabelData,
  options?: PrintLabelOptions | (() => void),
): Promise<void> {
  const resolved: PrintLabelOptions =
    typeof options === "function" ? { onPrinted: options } : (options ?? {});

  const format = resolved.format ?? loadSavedLabelFormat();
  const rotation = resolved.rotation ?? loadSavedLabelRotation();
  const mode = resolved.mode ?? "auto";

  if (mode === "tspl") {
    await printLabelDocumentDirect(label, format, rotation);
    resolved.onPrinted?.();
    return;
  }

  if (mode === "bridge") {
    await printLabelDocumentBridge(
      label,
      format,
      rotation,
      resolved.printerName,
      resolved.margins,
    );
    resolved.onPrinted?.();
    return;
  }

  if (mode === "auto") {
    const resolvedMode = await resolveAutoPrintMode();
    if (resolvedMode === "bridge") {
      await printLabelDocumentBridge(
        label,
        format,
        rotation,
        resolved.printerName,
        resolved.margins,
      );
      resolved.onPrinted?.();
      return;
    }
  }

  printLabelDocument(label, format, rotation, resolved.onPrinted);
}

/** @deprecated utiliser labelPrintSizeHint(format, rotation) */
export const LABEL_PRINT_SIZE_HINT = labelPrintSizeHint(
  loadSavedLabelFormat(),
  loadSavedLabelRotation(),
);
